import ErrorMessage from "@/v2-ui/ErrorMessage"
import { toast } from "@/v2-ui/toast"
import LicenseFeatureGroupAssignmentEditor from "@/v2-backoffice-client/license/featureGroup/LicenseFeatureGroupAssignmentEditor"
import LicenseFeatureGroupComposeSkeleton from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupComposeSkeleton"
import { useLicensePlanFeatureGroupsQuery } from "@/v2-backoffice-client/license/plan/view/license.plan.featureGroups.query"
import { useLicensePlanFeatureGroupsSetMutation } from "@/v2-backoffice-client/license/plan/update/license.plan.featureGroups.set.mutation"
import { evictPlanFeatureGroupQueries } from "@/v2-backoffice-client/license/license.utils"

export type LicensePlanFeaturesProps = {
  planId: number
  planName: string
}

// The license panel's Packages tab: what buying this license grants in the
// product, package by package — the answer to "does their license include X?"
// and the place that decision is made. Every package is a card to tick;
// hover one for what it grants and who else grants it; the footer writes
// the ticked set as one declaration. An empty mapping is a declared state,
// not an error: Fenerum knows what a customer pays for, this mapping is what
// that buys them in the product. Owns its query — the mapping loads when
// the tab is first opened, not when the panel is.
function LicensePlanFeatures(props: LicensePlanFeaturesProps) {
  const { planId, planName } = props

  const { data, previousData, loading, error } =
    useLicensePlanFeatureGroupsQuery({
      variables: { planId },
    })
  const featureGroups =
    (data ?? previousData)?.getLicensePlanFeatureGroups ?? []

  const [setFeatureGroups, { loading: isSaving, error: saveError }] =
    useLicensePlanFeatureGroupsSetMutation({
      update: evictPlanFeatureGroupQueries,
      onCompleted() {
        toast({
          variant: "success",
          title: "License packages saved",
          description: `What ${planName} grants was re-declared.`,
        })
      },
    })

  if (error) return <ErrorMessage className="p-4" error={error} />
  if (loading && !data && !previousData) {
    return <LicenseFeatureGroupComposeSkeleton columns={1} />
  }

  const persistedIds = featureGroups.map((group) => group.Id)

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <ErrorMessage className="text-center" error={saveError} />
      <LicenseFeatureGroupAssignmentEditor
        // Keyed on the persisted mapping: when the stored truth changes
        // under the editor (a save landing via evict, another operator's
        // edit), the selection reseeds from it.
        key={persistedIds.join()}
        className="min-h-0 flex-1"
        initialSelectedIds={persistedIds}
        isSaving={isSaving}
        onSave={(featureGroupIds) => {
          setFeatureGroups({ variables: { planId, featureGroupIds } })
        }}
      />
    </div>
  )
}

export default LicensePlanFeatures
